import type { Route } from "next";
import Link from "next/link";
import { useFormContext } from "react-hook-form";

import Button from "@/components/button";
import Icon from "@/components/icon";
import { useModalStore } from "@/stores/modal-store";

interface FormActionsProps {
  cancelHref?: string;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  isPending: boolean;
  isSuccess?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  closeModal?: () => void;
  cancelNavigationButtonClassName?: string;
  cancelModalButtonClassName?: string;
  cancelSubmitButtonClassName?: string;
}

const baseButtonClassName = "flex items-center justify-center px-4 py-2";

export const FormActions: React.FC<FormActionsProps> = ({
  cancelHref,
  submitLabel = "Valider",
  cancelLabel = "Annuler",
  isPending,
  isSuccess = false,
  disabled = false,
  style,
  cancelNavigationButtonClassName = `${baseButtonClassName} w-full`,
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  const rootError = errors.root;
  const handleClose = useModalStore((state) => state.handleClose);

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 justify-center mt-4 mb-4">
        {/* Bouton d'annulation - Navigation vers une page spécifique */}
        {cancelHref ? (
          <Link href={cancelHref as Route} className="w-full max-w-40">
            <Button
              variant="unfilled"
              colorTheme="secondary"
              className={cancelNavigationButtonClassName}
              disabled={isPending}
              style={style}
            >
              {cancelLabel}
            </Button>
          </Link>
        ) : cancelLabel === "hidden" ? (
          <></>
        ) : (
          /* Bouton d'annulation - Fermeture de modal */
          <Button
            variant="unfilled"
            colorTheme="secondary"
            className={`${baseButtonClassName} w-full max-w-40 border-1`}
            disabled={isPending}
            style={style}
            onClick={handleClose}
          >
            {cancelLabel}
          </Button>
        )}

        {/* Bouton de soumission - Validation du formulaire */}
        <Button
          type="submit"
          className={`${baseButtonClassName} w-full max-w-40 relative ${
            isSuccess ? "bg-success2 hover:bg-success2" : ""
          }`}
          disabled={isPending || disabled}
        >
          <span className={isPending || isSuccess ? "invisible" : ""}>
            {submitLabel}
          </span>
          {isPending && (
            <Icon
              icon="time"
              className="animate-spin absolute inset-0 m-auto"
              size={24}
            />
          )}
          {isSuccess && !isPending && (
            <Icon
              icon="check-simple"
              className="absolute inset-0 m-auto"
              size={24}
            />
          )}
        </Button>
      </div>
      {rootError?.message && (
        <div className="flex items-center gap-3 rounded-lg px-4 py-3 border border-red-button bg-danger3">
          <div className="flex-shrink-0">
            <Icon icon="bx-comment-error" className="h-5 w-5 text-red-button" />
          </div>
          <p className="text-sm font-medium text-red-button">
            {rootError.message}
          </p>
        </div>
      )}
    </>
  );
};
